import { useMutation, useQueryClient } from '@tanstack/react-query'
import { Choice } from '@/components/Choice'
import { FormError } from '@/components/FormError'
import { api, type Me } from '@/lib/api'
import { SECTION_LABELS } from '../../shared/sections'
import { ALL_POST, USE_LABELS, USE_SECTIONS, USES, type Use, type UseSection } from '../../shared/uses'

const HINTS: Record<Use, string> = {
  post: 'You add your own and see everyone’s.',
  view: 'You see what others share, with no “+” to add any.',
  off: 'Out of your tabs and Home. Anything someone sends you still opens.',
}

// What you use the app for, yourself: share and see, just see, or off, a section at a time. It's yours alone; nobody
// else sees a difference, and nothing you've added is hidden from anyone or deleted. The owner's switches and each circle's come
// first, so a section either has off isn't offered at all.
export function MySections({ me }: { me: Me }) {
  const queryClient = useQueryClient()
  const uses = me.uses ?? ALL_POST
  const offered = USE_SECTIONS.filter((s) => me.sections[s])
  const save = useMutation({
    mutationFn: (body: { section: UseSection; use: Use }) => api('/me/uses', { method: 'PATCH', body }),
    // The tabs change the moment you choose; if saving fails they go back to what the account has.
    onMutate: async ({ section, use }) => {
      await queryClient.cancelQueries({ queryKey: ['me'] })
      const before = me.uses
      queryClient.setQueryData<Me>(['me'], (m) => m && { ...m, uses: { ...(m.uses ?? ALL_POST), [section]: use } })
      return { before }
    },
    onError: (_error, _body, context) =>
      queryClient.setQueryData<Me>(['me'], (m) => m && context && { ...m, uses: context.before }),
  })
  if (offered.length === 0) return null
  return (
    <section aria-labelledby="what-i-use" className="flex flex-col gap-6 border-t pt-8">
      <div className="flex flex-col gap-1">
        <h2 id="what-i-use" className="text-title">
          What I use
        </h2>
        <p className="text-sub text-muted-foreground">Just for you, in every circle. Nobody else sees a difference.</p>
      </div>
      {offered.map((section) => (
        <Choice
          key={section}
          legend={SECTION_LABELS[section]}
          hint={HINTS[uses[section]]}
          value={uses[section]}
          options={USES.map((use) => [use, USE_LABELS[use]])}
          onChange={(use) => save.mutate({ section, use: use as Use })}
        />
      ))}
      <FormError error={save.error} />
    </section>
  )
}
